import React, { useState } from 'react';
import C from '../../data/colors';
import { importGuests, createEmailTemplate } from '../../data/api';
import { Upload, Mail } from 'lucide-react';

const templateTypes = ['save_the_date', 'invitation', 'thank_you', 'reminder', 'other'];

const UploadTab = ({ refresh }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  // Email template form
  const [tmplName, setTmplName] = useState('');
  const [tmplSubject, setTmplSubject] = useState('');
  const [tmplType, setTmplType] = useState('save_the_date');
  const [tmplHtml, setTmplHtml] = useState('');
  const [tmplMsg, setTmplMsg] = useState('');
  const [tmplLoading, setTmplLoading] = useState(false);

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/["']/g, ''));
    return lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/["']/g, ''));
      const row = {};
      headers.forEach((h, i) => { row[h] = vals[i] || ''; });
      return row;
    });
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const text = await file.text();
      const guests = parseCSV(text);
      if (guests.length === 0) { setResults({ created:0, duplicates:0, errors:['No valid rows found'] }); return; }
      const r = await importGuests(guests);
      setResults(r);
      refresh();
    } catch(err) {
      setResults({ created:0, duplicates:0, errors:[err.message] });
    } finally { setLoading(false); }
  };

  const handleCreateTemplate = async () => {
    if (!tmplName.trim() || !tmplSubject.trim() || !tmplHtml.trim()) {
      setTmplMsg('Please fill all required fields.');
      return;
    }
    setTmplLoading(true);
    try {
      await createEmailTemplate({ name: tmplName, subject: tmplSubject, template_type: tmplType, html_content: tmplHtml });
      setTmplMsg('Template created successfully!');
      setTmplName(''); setTmplSubject(''); setTmplHtml(''); setTmplType('save_the_date');
    } catch(err) {
      setTmplMsg('Failed to create template.');
    } finally { setTmplLoading(false); }
  };

  return (
    <div>
      {/* Import Guest List Section */}
      <h2 data-testid="import-heading" style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary, marginBottom: 8 }}>Import Guest List</h2>
      <p style={{ fontSize: 13, color: C.textSecondary, marginBottom: 16 }}>Upload a CSV or Excel file with columns: First Name, Last Name, Email, Phone (optional).</p>
      <label data-testid="choose-file-btn" style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px', borderRadius: 10,
        background: C.gold, color: '#000', fontSize: 14, cursor: 'pointer', fontWeight: 600,
        boxShadow: '0 2px 8px rgba(201,162,39,0.3)',
      }}>
        <Upload size={16} /> Choose File
        <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} style={{ display: 'none' }} />
      </label>
      {loading && <div style={{ marginTop: 12, color: C.gold }}>Processing...</div>}

      {results && (
        <div style={{ marginTop: 16, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
          <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
            <div><span style={{ fontSize: 24, fontWeight: 700, color: '#22c55e' }}>{results.created}</span><div style={{ fontSize: 12, color: C.textSecondary }}>Created</div></div>
            <div><span style={{ fontSize: 24, fontWeight: 700, color: '#f97316' }}>{results.duplicates}</span><div style={{ fontSize: 12, color: C.textSecondary }}>Duplicates</div></div>
            <div><span style={{ fontSize: 24, fontWeight: 700, color: C.error }}>{results.errors?.length || 0}</span><div style={{ fontSize: 12, color: C.textSecondary }}>Errors</div></div>
          </div>
          {results.errors?.length > 0 && (
            <div style={{ background: 'rgba(220,53,69,0.15)', borderRadius: 8, padding: 12 }}>
              {results.errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: '#ff6b6b', marginBottom: 4 }}>{e}</div>)}
            </div>
          )}
        </div>
      )}

      {/* Separator */}
      <div style={{ borderTop: `2px dashed ${C.border}`, margin: '32px 0' }} />

      {/* Create Email Template Section */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <Mail size={28} color={C.gold} />
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary, marginTop: 8, marginBottom: 4 }}>Create Email Template</h2>
        <p style={{ fontSize: 13, color: C.textSecondary }}>Create a new email template for invitations. Max file size: 50KB for HTML content.</p>
      </div>

      <div style={{ background: C.bgCard, borderRadius: 12, padding: 24, border: `1px solid ${C.border}` }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, display: 'block', marginBottom: 6 }}>Template Name *</label>
          <input data-testid="template-name-input" value={tmplName} onChange={e => setTmplName(e.target.value)} placeholder="e.g., Save The Date Email"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box', background: C.bgInput, color: C.textPrimary }} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, display: 'block', marginBottom: 6 }}>Email Subject *</label>
          <input data-testid="template-subject-input" value={tmplSubject} onChange={e => setTmplSubject(e.target.value)} placeholder="e.g., Save The Date - 80 Years of Connection"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box', background: C.bgInput, color: C.textPrimary }} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, display: 'block', marginBottom: 8 }}>Template Type:</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {templateTypes.map(t => (
              <button key={t} data-testid={`type-${t}`} onClick={() => setTmplType(t)} style={{
                padding: '8px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: tmplType === t ? C.gold : C.bgPrimary,
                color: tmplType === t ? '#000' : C.textSecondary,
                textTransform: 'uppercase',
              }}>
                {t.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, display: 'block', marginBottom: 6 }}>HTML Content *</label>
          <textarea data-testid="template-html-input" value={tmplHtml} onChange={e => setTmplHtml(e.target.value)}
            placeholder="(paste your email HTML here)"
            style={{ width: '100%', minHeight: 200, padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical', background: C.bgInput, color: C.textPrimary }} />
        </div>

        {tmplMsg && <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: 8, background: tmplMsg.includes('success') ? 'rgba(34,197,94,0.15)' : 'rgba(220,53,69,0.15)', color: tmplMsg.includes('success') ? '#22c55e' : '#ff6b6b', fontSize: 13 }}>{tmplMsg}</div>}

        <button data-testid="create-template-btn" onClick={handleCreateTemplate} disabled={tmplLoading} style={{
          width: '100%', padding: 14, borderRadius: 10, background: C.gold, color: '#000', border: 'none',
          fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: tmplLoading ? 0.6 : 1,
        }}>
          {tmplLoading ? 'Creating...' : 'Create Template'}
        </button>
      </div>
    </div>
  );
};

export default UploadTab;
